import styled from "styled-components";
import {brand, fontFamilies, goldEdgeFor, radii, shadows} from "../../theme/tokens";

/**
 * The recipient-cap upgrade nudge, anchored to the "Email to people" section header
 * (kitecorp/transferify-ui#2524; ui#213's header button, restored beside the field it is about after
 * ui#214 left the filled list with no upsell of its own). The root pins itself to the trailing edge of
 * the header row and is the positioning context for its own hover/focus tooltip.
 */
export const UpgradeNudgeRoot = styled.span`
	position: relative;
	margin-inline-start: auto;
	display: inline-flex;
	flex: none;
`;

/**
 * Compact gold "Upgrade" button; clicking opens the upgrade dialog on the target tier, hover/focus/tap
 * reveals the tip. The same `brand.gold` / `goldEdgeFor` pair the ProPill and the rest of the
 * subscription nudges wear, so every upgrade affordance in the composer reads as one family.
 */
export const UpgradeNudgeButton = styled.button`
	display: inline-flex;
	align-items: center;
	position: relative;
	min-height: 28px;
	padding: 3px 12px;
	border: none;
	outline: 1px solid ${({theme}) => goldEdgeFor(theme.tokens)};
	outline-offset: -1px;
	border-radius: ${radii.pill};
	background: ${brand.gold};
	color: ${brand.goldInk};
	font: 700 11.5px ${fontFamilies.sans};
	letter-spacing: 0.01em;
	cursor: pointer;
	transition: filter 140ms ease;

	/**
	 * A 44px POINTER TARGET around a control the header row keeps compact (WCAG 2.5.8). Grown as an
	 * overlay rather than by raising min-height, because this button shares a line with a 12px section
	 * label: inflating the control would push the whole recipients section down by the difference on
	 * every plan, including the Premium senders who never see this button. Transparent and inside the
	 * button, so the press it catches is this button's own; only the vertical axis needs the help,
	 * since the label's own width already clears 44px.
	 */
	&::after {
		content: "";
		position: absolute;
		left: 0;
		right: 0;
		top: 50%;
		height: 44px;
		transform: translateY(-50%);
	}

	&:hover {
		filter: brightness(0.97);
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The hover/focus-revealed offer. Right-anchored under the button and width-capped so it never
 * overflows a 375px card; `max-content` keeps short copy tight while `max-width` wraps long copy.
 */
export const UpgradeNudgeTip = styled.span`
	position: absolute;
	top: calc(100% + 8px);
	right: 0;
	z-index: 40;
	width: max-content;
	max-width: min(240px, 74vw);
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.panel};
	box-shadow: ${shadows.popover};
	padding: 9px 11px;
	font: 500 12px ${fontFamilies.sans};
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.ink2};
	white-space: normal;
	animation: nudgeTip 0.14s cubic-bezier(0.2, 0.8, 0.25, 1);

	@keyframes nudgeTip {
		from {
			opacity: 0;
			transform: translateY(-4px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}
`;
